/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import {
    Box,
    IconButton,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableFooter,
    TablePagination,
    Stack,
    TextField,
    Tooltip,
    Typography,
    CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import { Effect, FullTechnicianEntry, Material } from "../../utils/Interfaces";

export interface MaterialsTableProps {
    editing: boolean;
    technicianEntry: FullTechnicianEntry;
    showMaterialPrice: boolean;
    addMaterial: (material: Material, amount: number, action: Effect) => void;
    updateMaterial: (material: Material, amount: number, action: Effect) => void;
}

export const MaterialsTable = (props: MaterialsTableProps) => {
    const { editing, technicianEntry, updateMaterial, showMaterialPrice } = props;

    const [page, setPage] = useState<number>(0);
    const rowsPerPage: number = 5;

    const materials: Material[] = technicianEntry.materials;

    const [amounts, setAmounts] = useState<number[]>([]);

    const [loadingMaterialsChange, setLoadingMaterialsChange] = useState<boolean>(false);

    const showMaterialPriceComp = showMaterialPrice ?? false;

    const handleChangePage = (event: any, newPage: number) => {
        setPage(newPage);
    };

    useEffect(() => {
        let localAmounts: number[] = [];
        materials.map((material: Material) => localAmounts.push(material.amount));
        setAmounts(localAmounts);
    }, [technicianEntry, materials]);

    return (
        <>
            <TableContainer component={Paper}>
                <Table size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            {showMaterialPriceComp && (
                                <>
                                    <TableCell sx={{ width: "40%", paddingLeft: "8px" }}>
                                        <Typography fontWeight="bold">Material Name</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: "20%", paddingLeft: "8px" }}>
                                        <Typography fontWeight="bold">P/N</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: "15%", paddingLeft: "8px" }}>
                                        <Typography fontWeight="bold">Price/Unit</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: "15%", pl: "40px" }}>
                                        <Typography fontWeight="bold" align="center">
                                            Qty.
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: "10%", pl: "4px" }}>
                                        <Typography fontWeight="bold">Total Price</Typography>
                                    </TableCell>
                                </>
                            )}
                            {!showMaterialPriceComp && (
                                <>
                                    <TableCell sx={{ width: "50%", paddingLeft: "8px" }}>
                                        <Typography fontWeight="bold">Material Name</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: "25%", paddingLeft: "8px" }}>
                                        <Typography fontWeight="bold">P/N</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: "25%", pl: "40px" }}>
                                        <Typography fontWeight="bold">Qty.</Typography>
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {materials.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: any, index: number) => (
                            <TableRow
                                key={row.name}
                                sx={{
                                    "&:last-child td, &:last-child th": {
                                        border: 0,
                                    },
                                }}
                            >
                                <TableCell sx={{ pl: 1 }}>{row.name}</TableCell>
                                <TableCell sx={{ pl: 1 }}>{row.part_number}</TableCell>
                                {showMaterialPriceComp && <TableCell sx={{ pl: 1 }}>$ {row.price.toFixed(2)}</TableCell>}
                                <TableCell align={editing ? "left" : "center"}>
                                    {editing ? (
                                        <Box display="flex" flexDirection="row">
                                            <Stack direction="column" justifyContent="center">
                                                <TextField
                                                    value={amounts[index]}
                                                    type="number"
                                                    size="small"
                                                    sx={{ width: "100px" }}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                        let localAmounts: number[] = [];
                                                        amounts.map((globalAmount) => localAmounts.push(globalAmount));
                                                        localAmounts[index] = parseInt(event.target.value);
                                                        setAmounts(localAmounts);
                                                    }}
                                                />
                                            </Stack>
                                            {loadingMaterialsChange && (
                                                <CircularProgress size={24} sx={{ mt: 2, mb: 2, ml: 5, mr: 6 }} />
                                            )}
                                            {!loadingMaterialsChange && (
                                                <>
                                                    <Tooltip title="Update Material Amount">
                                                        <IconButton
                                                            sx={{ p: 2 }}
                                                            onClick={() => {
                                                                setLoadingMaterialsChange(true);
                                                                updateMaterial(row, amounts[index], () =>
                                                                    setLoadingMaterialsChange(false)
                                                                );
                                                            }}
                                                        >
                                                            <CheckIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Remove Material">
                                                        <IconButton
                                                            sx={{ p: 2 }}
                                                            onClick={() => {
                                                                setLoadingMaterialsChange(true);
                                                                updateMaterial(row, 0, () => setLoadingMaterialsChange(false));
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box sx={{ ml: 2 }}>{row.amount}</Box>
                                    )}
                                </TableCell>
                                {showMaterialPriceComp && (
                                    <TableCell sx={{ pl: 1 }}>$ {(row.price * row.amount).toFixed(2)}</TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                colSpan={showMaterialPriceComp ? 5 : 3}
                                count={materials.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                labelRowsPerPage=""
                                onPageChange={handleChangePage}
                                showFirstButton={false}
                                showLastButton={false}
                                SelectProps={{
                                    sx: { display: "none" },
                                }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </>
    );
};
